import PrintableDocument from './PrintableDocument';

export interface FicheVisitePlainte {
  date: string;
  description: string;
  source: string;
}

export interface FicheVisiteSoin {
  date: string;
  description: string;
  source: string;
  statut: string;
}

export interface FicheVisiteBilanLabo {
  analyses: string;
  resultats: string | null;
  date: string;
  source: string;
}

interface PrintableFicheVisiteProps {
  patientName: string;
  patientHeader: string;
  allergies: string;
  age: number;
  sexe: string;
  numeroDossier: string;
  groupeSanguin: string;
  entryDate: string;
  entryHeure: string;
  medecinNom: string;
  medecinSpecialite: string | null;
  constantes: { label: string; value: string; icon: string }[];
  antecedents: { label: string; value: string }[];
  symptomes: string;
  examen: string;
  plaintes: FicheVisitePlainte[];
  soins: FicheVisiteSoin[];
  bilanLabo: FicheVisiteBilanLabo[];
  diagnostic: string;
  practitionerName: string;
  practitionerTitle?: string;
  date: string;
}

function SectionTitle({ icon, children }: { icon: string; children: React.ReactNode }) {
  return (
    <div className="print-section-title">
      <i className={icon} style={{ marginRight: '6px', fontSize: '14px' }}></i>
      {children}
    </div>
  );
}

function EmptyHint({ text }: { text: string }) {
  return (
    <div className="print-text" style={{ color: '#999', fontStyle: 'italic' }}>
      {text}
    </div>
  );
}

export default function PrintableFicheVisite({
  patientName,
  patientHeader,
  allergies,
  age,
  sexe,
  numeroDossier,
  groupeSanguin,
  entryDate,
  entryHeure,
  medecinNom,
  medecinSpecialite,
  constantes,
  antecedents,
  symptomes,
  examen,
  plaintes,
  soins,
  bilanLabo,
  diagnostic,
  practitionerName,
  practitionerTitle,
  date,
}: PrintableFicheVisiteProps) {
  const antecedentsRemplis = antecedents.filter((a) => a.value && a.value.trim());

  return (
    <PrintableDocument
      documentTitle="Fiche de visite"
      patientName={patientName}
      practitionerName={practitionerName}
      practitionerTitle={practitionerTitle}
      date={date}
      letterhead
    >
      {/* En-tête patient */}
      <div style={{ marginBottom: '5mm', padding: '3mm', background: '#f0fdf4', borderRadius: '4px', border: '1px solid #bbf7d0' }}>
        <div style={{ fontSize: '12pt', fontWeight: 'bold', color: '#166534', marginBottom: '1mm' }}>
          {patientName}
        </div>
        <div style={{ fontSize: '9pt', color: '#374151' }}>
          {age} ans · {sexe} · N° dossier {numeroDossier}
          {groupeSanguin ? ` · GS ${groupeSanguin}` : ''}
        </div>
        {allergies && (
          <div style={{ fontSize: '9pt', color: '#b91c1c', marginTop: '1mm', fontWeight: 'bold' }}>
            Allergies : {allergies}
          </div>
        )}
      </div>

      {/* Admission */}
      <div style={{ marginBottom: '4mm' }}>
        <strong>Admission :</strong> {entryDate}
        {entryHeure ? ` à ${entryHeure}` : ''} · {medecinNom}
        {medecinSpecialite ? ` · ${medecinSpecialite}` : ''}
      </div>

      {/* Constantes */}
      {constantes.length > 0 && (
        <>
          <SectionTitle icon="ri-dashboard-fill">Constantes à l'admission</SectionTitle>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3mm', marginBottom: '4mm' }}>
            {constantes.map((c) => (
              <div key={c.label} style={{ padding: '2mm 3mm', background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '3px', fontSize: '9pt' }}>
                <span style={{ color: '#6b7280', fontWeight: 600 }}>{c.label} :</span>{' '}
                <span style={{ fontWeight: 'bold', color: '#111827' }}>{c.value}</span>
              </div>
            ))}
          </div>
        </>
      )}

      {/* Antécédents */}
      <SectionTitle icon="ri-health-book-fill">Antécédents médicaux</SectionTitle>
      {antecedentsRemplis.length === 0 ? (
        <EmptyHint text="Aucun antécédent renseigné." />
      ) : (
        <div style={{ marginBottom: '4mm' }}>
          {antecedentsRemplis.map((a, i) => (
            <div className="print-text" key={i} style={{ marginBottom: '1.5mm' }}>
              <strong>{a.label} :</strong> {a.value}
            </div>
          ))}
        </div>
      )}

      {/* Symptômes */}
      {symptomes && (
        <>
          <SectionTitle icon="ri-emotion-fill">Symptômes & signes (admission)</SectionTitle>
          <div className="print-text" style={{ marginBottom: '4mm', whiteSpace: 'pre-wrap' }}>
            {symptomes}
          </div>
        </>
      )}

      {/* Examen */}
      {examen && (
        <>
          <SectionTitle icon="ri-search-eye-fill">Examen clinique</SectionTitle>
          <div className="print-text" style={{ marginBottom: '4mm', whiteSpace: 'pre-wrap' }}>
            {examen}
          </div>
        </>
      )}

      {/* Plaintes */}
      <SectionTitle icon="ri-emotion-fill">Plaintes & symptômes</SectionTitle>
      {plaintes.length === 0 ? (
        <EmptyHint text="Aucune plainte enregistrée." />
      ) : (
        <table className="print-table" style={{ marginBottom: '4mm' }}>
          <thead>
            <tr>
              <th style={{ width: '110px' }}>Date</th>
              <th style={{ width: '100px' }}>Origine</th>
              <th>Plainte / Symptômes</th>
            </tr>
          </thead>
          <tbody>
            {plaintes.map((p, i) => (
              <tr key={i}>
                <td>{p.date ? new Date(p.date).toLocaleDateString('fr-FR') : '—'}</td>
                <td>{p.source}</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>{p.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Soins */}
      <SectionTitle icon="ri-nurse-fill">Soins administrés</SectionTitle>
      {soins.length === 0 ? (
        <EmptyHint text="Aucun soin enregistré." />
      ) : (
        <table className="print-table" style={{ marginBottom: '4mm' }}>
          <thead>
            <tr>
              <th style={{ width: '110px' }}>Date</th>
              <th style={{ width: '100px' }}>Origine</th>
              <th>Soin / Acte</th>
            </tr>
          </thead>
          <tbody>
            {soins.map((s, i) => (
              <tr key={i}>
                <td>{s.date ? new Date(s.date).toLocaleDateString('fr-FR') : '—'}</td>
                <td>{s.source}</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>{s.description}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Bilan laboratoire */}
      <SectionTitle icon="ri-microscope-fill">Bilan du laboratoire</SectionTitle>
      {bilanLabo.length === 0 ? (
        <EmptyHint text="Aucun résultat de laboratoire." />
      ) : (
        <table className="print-table" style={{ marginBottom: '4mm' }}>
          <thead>
            <tr>
              <th style={{ width: '110px' }}>Date</th>
              <th style={{ width: '140px' }}>Analyses</th>
              <th>Résultats / Conclusion</th>
            </tr>
          </thead>
          <tbody>
            {bilanLabo.map((b, i) => (
              <tr key={i}>
                <td>{b.date ? new Date(b.date).toLocaleDateString('fr-FR') : '—'}</td>
                <td>{b.analyses}</td>
                <td style={{ whiteSpace: 'pre-wrap' }}>{b.resultats || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Diagnostic */}
      <SectionTitle icon="ri-mental-health-fill">Diagnostic</SectionTitle>
      {diagnostic ? (
        <div className="print-text" style={{ marginBottom: '4mm', padding: '3mm', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '3px', color: '#991b1b', fontWeight: 600 }}>
          {diagnostic}
        </div>
      ) : (
        <EmptyHint text="Aucun diagnostic enregistré." />
      )}

      {/* Signature */}
      <div style={{ marginTop: '10mm', textAlign: 'right' }}>
        <div style={{ fontSize: '10pt', color: '#666' }}>{practitionerName}</div>
        {practitionerTitle && <div style={{ fontSize: '9pt', color: '#999' }}>{practitionerTitle}</div>}
        <div style={{ fontSize: '8pt', color: '#aaa', marginTop: '1mm' }}>
          Fiche de visite générée le {date}
        </div>
      </div>
    </PrintableDocument>
  );
}